import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Wind, Sparkles, ChevronRight, Play, Feather, Share2 } from 'lucide-react';
import { useContent } from '../context/ContentContext';
import { getManifestTheme } from '../data/manifestData';
import ShareSheet from '../components/ShareSheet';

const GRIEF_BREATH = [
  { id: 'wave-release',   title: 'Wave Release',    tagline: '4 in · 8 out · Let the wave pass through', accent: '#789B83', to: '/workplace-reset/workday-release' },
  { id: 'sob-safe',       title: 'Sob-Safe Breath', tagline: 'Slow box breath — steady the chest when it aches', accent: '#5B7A65', to: '/workplace-reset/box-breath' },
  { id: 'shoulder-drop',  title: 'Shoulder Drop',   tagline: 'Let go of everything you have been carrying', accent: '#A3BEAF', to: '/workplace-reset/shoulder-release' },
];

const GRIEF_AFFIRMATIONS = [
  { id: 'healing-and-release', title: 'Healing & Release' },
  { id: 'peace',               title: 'Peace' },
  { id: 'self-worth',          title: 'You Are Not Alone' },
];

const GriefPage = () => {
  const { posts } = useContent();
  // Every meditation surfaced here needs a share affordance — the user
  // reported the button was missing on curated lists like this.
  const [sharePost, setSharePost] = useState(null);
  const shareContent = (p) => ({
    kind: 'post',
    id: p.id,
    title: p.title,
    text: `"${p.title}" — a reflection on Reflections by Rowdy`,
    url: typeof window !== 'undefined' ? `${window.location.origin}/post/${p.id}` : '',
    recommendable: true,
  });

  const griefPosts = React.useMemo(() => {
    if (!posts?.length) return [];
    const cue = /(grief|mourn|loss|goodbye|missing you|honoring|remembr)/i;
    const themed = posts.filter((p) => cue.test(`${p.title} ${p.excerpt || ''}`));
    if (themed.length >= 3) return themed.slice(0, 4);
    const guided = posts.filter((p) => p.categoryId === 'guided-meditations').slice(0, 4);
    return [...themed, ...guided.filter((g) => !themed.some((t) => t.id === g.id))].slice(0, 4);
  }, [posts]);

  const featured = getManifestTheme('healing-and-release');

  return (
    <div className="pb-8" data-testid="grief-page">
      <div className="px-5 pt-6 pb-4 animate-fade-in">
        <p className="rbr-eyebrow mb-2" style={{ color: '#D6AD60' }}>Grief</p>
        <h1 className="rbr-heading text-3xl mb-1 leading-tight" style={{ color: '#F8F6F0' }}>
          You don&rsquo;t have to be okay today.
        </h1>
        <p className="text-sm" style={{ color: '#A3BEAF' }}>
          Words, breath, and space — for when the wave is here.
        </p>
      </div>

      {featured && featured.affirmations?.[0] && (
        <div className="px-5 mb-5 animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
          <Link
            to={`/manifest/${featured.id}`}
            data-testid="grief-affirmation-featured"
            className="block rounded-2xl p-5 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all bg-[#1F4C40] border border-[#2E6353]"
          >
            <div className="flex items-center gap-2 mb-2">
              <Feather className="w-3.5 h-3.5" style={{ color: '#D6AD60' }} />
              <p className="rbr-eyebrow">For today</p>
            </div>
            <blockquote
              className="mb-1"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.15rem', fontWeight: 500, color: '#F8F6F0', lineHeight: 1.35 }}
            >
              &ldquo;{featured.affirmations[0].title}.&rdquo;
            </blockquote>
            <p className="text-[11px] mt-2 flex items-center gap-1" style={{ color: '#A3BEAF' }}>
              Read all 30 <ChevronRight className="w-3 h-3" />
            </p>
          </Link>
        </div>
      )}

      {griefPosts.length > 0 && (
        <div className="px-5 mb-6 animate-fade-in-up" style={{ animationDelay: '0.08s' }}>
          <div className="flex items-center gap-2 mb-3">
            <Heart className="w-3.5 h-3.5" style={{ color: '#D6AD60' }} />
            <p className="rbr-eyebrow">Sit With It</p>
          </div>
          <div className="space-y-2">
            {griefPosts.map((p) => (
              <div key={p.id} className="relative">
                <Link
                  to={`/post/${p.id}`}
                  data-testid={`grief-post-${p.id}`}
                  className="flex items-center gap-3 rounded-2xl p-3 pr-14 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all bg-[#1F4C40] border border-[#2E6353]"
                >
                  {p.image ? (
                    <img src={p.image} alt="" loading="lazy" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-[#173F35] flex items-center justify-center flex-shrink-0">
                      <Play className="w-4 h-4" style={{ color: '#D6AD60' }} fill="currentColor" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#F8F6F0] truncate">{p.title}</p>
                    <p className="text-[11px] text-[#A3BEAF] truncate">
                      {p.category}{p.duration ? ` · ${p.duration}` : ''}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 flex-shrink-0 text-[#7A9B8C]" />
                </Link>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSharePost(p); }}
                  data-testid={`grief-share-${p.id}`}
                  aria-label={`Share ${p.title}`}
                  title="Share"
                  className="absolute right-9 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center bg-[#173F35] hover:bg-[#D6AD60]/20 border border-[#2E6353] transition-colors"
                >
                  <Share2 className="w-3.5 h-3.5" style={{ color: '#D6AD60' }} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="px-5 mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
        <div className="flex items-center gap-2 mb-3">
          <Wind className="w-3.5 h-3.5" style={{ color: '#D6AD60' }} />
          <p className="rbr-eyebrow">Breath For Grief</p>
        </div>
        <div className="space-y-2">
          {GRIEF_BREATH.map((b) => (
            <Link
              key={b.id}
              to={b.to}
              data-testid={`grief-breath-${b.id}`}
              className="flex items-center gap-3 rounded-2xl p-3.5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all bg-[#1F4C40] border border-[#2E6353]"
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                   style={{ background: `${b.accent}33`, boxShadow: `inset 0 0 0 1px ${b.accent}77` }}>
                <Wind className="w-4 h-4" style={{ color: b.accent }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#F8F6F0]">{b.title}</p>
                <p className="text-[11px] text-[#A3BEAF]">{b.tagline}</p>
              </div>
              <ChevronRight className="w-4 h-4 flex-shrink-0 text-[#7A9B8C]" />
            </Link>
          ))}
        </div>
      </div>

      <div className="px-5 mb-2 animate-fade-in-up" style={{ animationDelay: '0.12s' }}>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-3.5 h-3.5" style={{ color: '#D6AD60' }} />
          <p className="rbr-eyebrow">Words That Hold You</p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {GRIEF_AFFIRMATIONS.map((a) => (
            <Link
              key={a.id}
              to={`/manifest/${a.id}`}
              data-testid={`grief-affirm-${a.id}`}
              className="flex flex-col items-center gap-2 p-3 rounded-2xl transition-all hover:-translate-y-0.5 bg-[#1F4C40] border border-[#2E6353]"
            >
              <Sparkles className="w-3.5 h-3.5" style={{ color: '#D6AD60' }} />
              <span className="text-[11px] font-semibold text-center leading-tight text-[#F8F6F0]">
                {a.title}
              </span>
            </Link>
          ))}
        </div>
      </div>

      <ShareSheet
        open={!!sharePost}
        onClose={() => setSharePost(null)}
        content={sharePost ? shareContent(sharePost) : undefined}
      />
    </div>
  );
};

export default GriefPage;
